import React, { useRef } from 'react';
import { Line } from 'react-konva';
import { string, number, arrayOf, shape, bool, func, object } from 'prop-types';
import { connect } from 'react-redux';

import { getPosDragBoundFunc, getStrokeDash } from 'src/utils/canvasHelpers';
import {
  useMoveTransformElement,
  useKeyboardListener,
  useOnClickElement,
  useDraggableElement,
  useElementColor,
  useDecreaseCanvasSize,
} from 'src/hooks';
import { SHIFT_CHARACTER_CODE } from 'src/constants/keyboardCodes';
import { LINE_ELEMENT } from 'src/constants/canvasElements';

const CanvasLineComponent = ({
  uuid,
  x,
  y,
  points,
  width,
  height,
  scaleX,
  skewX,
  skewY,
  borderColor: { hex: borderHex },
  strokeWidth,
  selectedElement: selectedElementId,
  isInGroup,
  rotation,
  selectedRef,
  scale,
  eyeDropper,
  onTransformElement,
  hideTransformerAnchors,
  showTransformerAnchors,
  strokeDash = [],
  shadowEnabled = false,
  shadowOffsetX,
  shadowOffsetY,
  shadowBlur,
  shadowColor = { alpha: 0 },
  pinchZoomGesture,
}) => {
  const refLine = useRef();
  const refPositions = useRef({ positions: [], posIndex: 0 });

  const {
    moveElementRequest,
    transformElementRequest,
    selectElementRequest,
    onDragMoveScroll,
    onDragEndScroll,
    allowDraggingElement,
  } = useMoveTransformElement(selectedElementId, uuid, refLine, selectedRef);

  const shiftShortcutPressed = useKeyboardListener(SHIFT_CHARACTER_CODE);

  const onDragStart = (e) => {
    if (!isInGroup) {
      const canDrag = allowDraggingElement(e);
      if (!canDrag) {
        return;
      }
      const isSelected = selectedElementId === uuid;
      !isSelected && selectElementRequest(e);
      hideTransformerAnchors();
    }
  };

  const onDragEnd = (e) => {
    if (!isInGroup && e.evt) {
      moveElementRequest(e);
      showTransformerAnchors();
      onDragEndScroll();
    }
  };

  const onDragMove = (e) => {
    if (!isInGroup) {
      onDragMoveScroll(e);
    }
  };

  const { onClick } = useOnClickElement(
    isInGroup,
    selectedElementId !== uuid,
    selectElementRequest,
  );

  const { draggableElement } = useDraggableElement(isInGroup);

  const { borderColor, dropShadow } = useElementColor(
    selectedElementId === uuid,
    eyeDropper,
    LINE_ELEMENT,
    { border: borderHex, shadow: shadowColor },
  );

  useDecreaseCanvasSize(refLine, isInGroup);

  return (
    <Line
      id={uuid}
      ref={refLine}
      x={x}
      y={y}
      points={points}
      stroke={borderColor}
      scaleX={scaleX}
      skewX={skewX}
      skewY={skewY}
      strokeWidth={strokeWidth}
      hitStrokeWidth={strokeWidth <= 5 ? 15 : strokeWidth + 10}
      dash={getStrokeDash(strokeDash, strokeWidth, scale)}
      width={width}
      height={height}
      offsetX={width / 2}
      draggable={draggableElement}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragMove={onDragMove}
      onClick={onClick}
      onTap={onClick}
      onTransformEnd={!isInGroup && transformElementRequest}
      onTransform={onTransformElement}
      rotation={rotation}
      dragBoundFunc={(pos) => getPosDragBoundFunc(
        refPositions.current,
        pos,
        1 / scale,
        refLine.current?.absolutePosition(),
        shiftShortcutPressed,
      )}
      shadowOpacity={dropShadow.alpha / 100}
      shadowColor={dropShadow.hex}
      shadowBlur={shadowBlur}
      shadowOffset={{ x: shadowOffsetX, y: shadowOffsetY }}
      shadowEnabled={shadowEnabled}
      elementType={LINE_ELEMENT}
      listening={!pinchZoomGesture && !eyeDropper.active}
      perfectDrawEnabled={false}
    />
  );
};

CanvasLineComponent.propTypes = {
  uuid: string.isRequired,
  x: number.isRequired,
  y: number.isRequired,
  borderColor: shape({
    hex: string,
    alpha: number,
  }).isRequired,
  scaleX: number.isRequired,
  skewX: number,
  skewY: number,
  points: arrayOf(number).isRequired,
  width: number,
  height: number,
  strokeWidth: number,
  selectedElement: string,
  isInGroup: bool,
  rotation: number.isRequired,
  selectedRef: object,
  scale: number,
  eyeDropper: shape({ active: bool }),
  onTransformElement: func,
  hideTransformerAnchors: func,
  showTransformerAnchors: func,
  strokeDash: arrayOf(number),
  shadowOffsetX: number,
  shadowOffsetY: number,
  shadowBlur: number,
  shadowColor: shape({
    hex: string,
    alpha: number,
  }),
  shadowEnabled: bool,
  pinchZoomGesture: bool,
};

const mapStateToProps = (store, ownProps) => ({
  selectedElement: store.project.present.selectedElements[ownProps.uuid],
  selectedRef: store.project.present.selectedRefs[ownProps.uuid],
  scale: store.canvas.scale,
  eyeDropper: store.canvas.eyeDropper,
  pinchZoomGesture: store.canvas.pinchZoomGesture,
});

const CanvasLine = connect(mapStateToProps)(CanvasLineComponent);

export { CanvasLine };
